import { FieldArray, FieldArrayRenderProps } from "formik"
import { useState } from "react"
import { Button, Card, Col, Form, Row } from "react-bootstrap"
import { PROVIDER_OPTIONS, PROVIDER_SETTING_LEVEL_OPTIONS, ProviderSettingType } from "../../../Types/amenityTypes"
import VRBOAmenityMappingModal from "../../Modals/VRBOAmenityMappingModal/VRBOAmenityMappingModal"

const blankProviderSetting = (): ProviderSettingType => ({
    provider: 'vrbo',
    level: 'property',
    key: '',
    available: true,
    values: { fields: [] },
})

// Custom QuickForm field for editing an amenity's providerSettings array.
// Each entry maps this amenity to a provider (vrbo, custom, etc) at a given level (property/unit).
export default function ProviderSettingsField(props: { fieldName: string, formik: any }) {
    const { fieldName, formik } = props
    const { values, setFieldValue } = formik
    const providerSettings: ProviderSettingType[] = values[fieldName] || []

    const [mappingModalIdx, setMappingModalIdx] = useState<number | null>(null)
    const activeMappingSetting = mappingModalIdx !== null ? providerSettings[mappingModalIdx] : null

    return (
        <div className="provider-settings-field">
            <FieldArray
                name={fieldName}
                render={(arrayHelpers: FieldArrayRenderProps) => (
                    <>
                        {providerSettings.map((setting, idx) => {
                            const fieldCount = setting.values?.fields?.length || 0
                            return (
                                <Card key={`provider-setting-${idx}`} className="mb-3">
                                    <Card.Body>
                                        <Row>
                                            <Col xs={12} md={3}>
                                                <Form.Group className="mb-2">
                                                    <Form.Label>Provider</Form.Label>
                                                    <Form.Select
                                                        value={setting.provider}
                                                        onChange={(e) => setFieldValue(`${fieldName}[${idx}].provider`, e.target.value)}
                                                    >
                                                        {PROVIDER_OPTIONS.map(opt => (
                                                            <option key={opt.value} value={opt.value}>{opt.text}</option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col xs={12} md={3}>
                                                <Form.Group className="mb-2">
                                                    <Form.Label>Level</Form.Label>
                                                    <Form.Select
                                                        value={setting.level}
                                                        onChange={(e) => setFieldValue(`${fieldName}[${idx}].level`, e.target.value)}
                                                    >
                                                        {PROVIDER_SETTING_LEVEL_OPTIONS.map(opt => (
                                                            <option key={opt.value} value={opt.value}>{opt.text}</option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col xs={12} md={4}>
                                                <Form.Group className="mb-2">
                                                    <Form.Label>Provider Key</Form.Label>
                                                    <Form.Control
                                                        value={setting.key}
                                                        placeholder="ex: dvd-player"
                                                        onChange={(e) => setFieldValue(`${fieldName}[${idx}].key`, e.target.value)}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col xs={12} md={2} className="d-flex align-items-center">
                                                <Form.Group className="mb-2">
                                                    <Form.Check
                                                        type="checkbox"
                                                        label="Available"
                                                        checked={!!setting.available}
                                                        onChange={(e) => setFieldValue(`${fieldName}[${idx}].available`, e.target.checked)}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        {setting.provider === 'vrbo' && (
                                            <div className="mb-2">
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="secondary"
                                                    onClick={() => setMappingModalIdx(idx)}
                                                >
                                                    Configure Vrbo Mapping {fieldCount > 0 ? `(${fieldCount} field${fieldCount === 1 ? '' : 's'})` : ''}
                                                </Button>
                                            </div>
                                        )}

                                        {setting.provider === 'custom' && (
                                            <Form.Group className="mb-2">
                                                <Form.Label>Values (JSON)</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={3}
                                                    defaultValue={setting.values ? JSON.stringify(setting.values, null, 2) : ''}
                                                    onBlur={(e: any) => {
                                                        try {
                                                            const parsed = e.target.value ? JSON.parse(e.target.value) : undefined
                                                            setFieldValue(`${fieldName}[${idx}].values`, parsed)
                                                        } catch (err) {
                                                            // ignore invalid JSON, leave previous stored value in place
                                                        }
                                                    }}
                                                />
                                            </Form.Group>
                                        )}

                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="danger"
                                            onClick={() => arrayHelpers.remove(idx)}
                                        >
                                            - Remove
                                        </Button>
                                    </Card.Body>
                                </Card>
                            )
                        })}

                        <Button
                            type="button"
                            size="sm"
                            className="mb-3"
                            onClick={() => arrayHelpers.push(blankProviderSetting())}
                        >
                            + Add Provider Setting
                        </Button>

                        {activeMappingSetting && mappingModalIdx !== null && (
                            <VRBOAmenityMappingModal
                                active={true}
                                level={activeMappingSetting.level === 'unit' ? 'unit' : 'property'}
                                initialKey={activeMappingSetting.key}
                                initialAvailable={activeMappingSetting.available}
                                initialValues={activeMappingSetting.values}
                                onClose={() => setMappingModalIdx(null)}
                                onSave={(result) => {
                                    setFieldValue(`${fieldName}[${mappingModalIdx}].key`, result.key)
                                    setFieldValue(`${fieldName}[${mappingModalIdx}].available`, result.available)
                                    setFieldValue(`${fieldName}[${mappingModalIdx}].values`, result.values)
                                    setMappingModalIdx(null)
                                }}
                            />
                        )}
                    </>
                )}
            />
        </div>
    )
}
